import {FormFieldValidationResult} from '@mintdata-wsp/common/components/form/Form';
import {IFormFieldState} from '@mintdata-wsp/common/components/formField/FormField';
import {AnalyticsEventActions} from '@mintdata-wsp/common/controllers/analytics/AnalyticsEventActions';
import {ApiError} from '@mintdata-wsp/common/controllers/network/Fetcher';
import {CheckCustomerCNameRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/CheckCustomerCNameRequest';
import {CheckOurCNameProgress} from '@mintdata-wsp/common/controllers/network/generated/requests/CheckOurCNameProgress';
import {PublishRequest} from '@mintdata-wsp/common/controllers/network/generated/requests/PublishRequest';
import {CheckResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/CheckResponse';
import {CNameMappingResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/CNameMappingResponse';
import {PublishedApplicationResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/PublishedApplicationResponse';
import {PublishedDesignResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/PublishedDesignResponse';
import {SuggestUrlPartResponse} from '@mintdata-wsp/common/controllers/network/generated/responses/SuggestUrlPartResponse';
import {API} from '@mintdata-wsp/common/controllers/network/WorkspaceAPI';
import {Project} from '@mintdata-wsp/common/state/DataState';
import {rootState} from '@mintdata-wsp/common/state/RootState';
import {
    CNameMappingStatus,
    ConnectDomainState,
    DesignRef,
    PublishMode,
    PublishStatus,
    PublishUIRequest
} from '@mintdata-wsp/common/state/ui/publish/PublishState';
import {PublishState} from '@mintdata-wsp/common/state/ui/visualDesigner/assets/DesignAssetsState';
import {WindowLocationUtil} from '@mintdata-wsp/common/util/WindowLocationUtil';
import {Dialogs} from '@mintdata-wsp/wsp/controllers/common/DialogsController';
import {controllers} from '@mintdata-wsp/wsp/controllers/WspControllers';
import {IPublishDialogProps} from '@mintdata-wsp/wsp/ui/dialogs/publish/PublishDialog';
import {PublishedAppUrlUtil} from '@mintdata-wsp/wsp/util/PublishedAppUrlUtil';
import {GUID} from '@mintdata/mint-kit/api/models/GUID';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import bind from 'bind-decorator';
import {action} from 'mobx';

export class PublishController
{
    public init()
    {
    }

    @action
    private _fetchUrlPartSuggestions(designId:number)
    {
        API.publish.suggestUrlPart(designId)
            .then(this._setupUrlParts)
            .catch(this._setupUrlPartsFallback);
    }

    @action.bound
    private _setupUrlParts(response:SuggestUrlPartResponse)
    {
        const {request} = rootState.ui.publish;

        if( !request.urlPart )
            request.urlPart = response.urlPart;

        if( !request.betaUrlPart )
            request.betaUrlPart = response.betaUrlPart;
    }

    @action.bound
    private _setupUrlPartsFallback()
    {
        const designName = rootState.ui.publish.request.designRef.designName;
        const {request} = rootState.ui.publish;

        if( !request.urlPart )
            request.urlPart = this._designNameToSafeUrlPart(designName);

        if( !request.betaUrlPart )
            request.betaUrlPart = 'beta-' + this._designNameToSafeUrlPart(designName);
    }

    @bind
    private _onCNameMappingResponse(resp:CNameMappingResponse)
    {
        if( resp.cnameMappingStatus === CNameMappingStatus.Done )
        {
            this.applyDomainSettings();
        }
        else if( resp.cnameMappingStatus === CNameMappingStatus.OurSetupInProgress )
        {
            if( resp.checkStatusId )
            {
                this.setCnameCheckId(resp.checkStatusId);
                this.checkOurCNameStatus();
            }
            else
                setTimeout(() => this.checkOurCNameStatus(), 300);
        }
        else
            this.setDomainSettingsFailure();
    }

    @action
    public checkOurCNameStatus():void
    {
        const cnameCheckId:string = rootState.ui.publish.cnameCheckId;

        if( !cnameCheckId )
            return;

        API.publish.checkOurCNameProgress(new CheckOurCNameProgress(cnameCheckId))
            .then(this._onCNameMappingResponse)
            .catch(this.setDomainSettingsFailure);
    }

    @action
    public checkCustomerCNameStatus():void
    {
        const publishState = rootState.ui.publish;

        publishState.connectDomainState = ConnectDomainState.Checking;

        const cnameHostname = publishState.cnameHostname;
        const urlPart = publishState.request.urlPart;

        API.publish.checkCustomerCNameMapping(new CheckCustomerCNameRequest(cnameHostname, urlPart))
            .then(this._onCNameMappingResponse)
            .catch(this.setDomainSettingsFailure);
    }

    @action.bound
    public showPublishDialog(props:IPublishDialogProps, shouldSaveBeforePublish:boolean = false)
    {
        const uiRequest = this._createDialogState(props);

        rootState.ui.publish.progress.isPublished = false;
        rootState.ui.publish.progress.hadErrorDuringPublish = false;
        rootState.ui.publish.progress.publishProgressMessage = null;
        rootState.ui.publish.shouldSaveBeforePublish = shouldSaveBeforePublish;

        rootState.ui.publish.request = uiRequest;

        this._fetchUrlPartSuggestions(uiRequest.designRef.designId);

        controllers.ui.dialogs.showDialog(Dialogs.Publish, false, props);
    }

    public hidePublishDialog()
    {
        controllers.ui.dialogs.hideDialog(Dialogs.Publish);
    }

    @bind
    private _createDialogState(props:IPublishDialogProps):PublishUIRequest
    {
        const openDesign = controllers.ui.openDesign.getOpenDesign();

        let mode:PublishMode;
        let urlPart:string;
        let betaUrlPart:string;
        let isBeta:boolean;

        if( openDesign )
        {
            const {designState: {assets: {publishState}}} = openDesign;

            mode = publishState.mode;
            urlPart = publishState.urlPart;
            betaUrlPart = publishState.betaUrlPart;
            isBeta = publishState.isBeta;
        }
        else
        {
            urlPart = this._getUrlPartIfPublished({isBeta: false, designRef: props.designRef});
            betaUrlPart = this._getUrlPartIfPublished({isBeta: true, designRef: props.designRef});
        }

        const req = new PublishUIRequest();

        req.mode = mode || PublishMode.MintDataOrganizationLogin;
        req.urlPart = urlPart || null;
        req.betaUrlPart = betaUrlPart || null;
        req.isBeta = isBeta || false;
        req.designRef = props.designRef;
        req.cnameHostname = '';

        return req;
    }

    public computeFullUrl(value?:string):string
    {
        const {isBeta} = rootState.ui.publish.request;
        const {siteId} = rootState.userSession.currentOrganization;
        const urlPart = value || rootState.ui.publish.request.currentUrlPart;

        return PublishedAppUrlUtil.getFullUrl(siteId, urlPart, isBeta);
    }

    public computeUrlPrefix():string
    {
        const {isBeta} = rootState.ui.publish.request;
        return PublishedAppUrlUtil.getUrlPrefix(isBeta);
    }

    public computeUrlPostfix():string
    {
        const {siteId} = rootState.userSession.currentOrganization;
        return PublishedAppUrlUtil.getUrlPostfix(siteId);
    }

    @action
    private _setUrlPartToUniqueId()
    {
        const uniqueId:GUID = IdUtil.newGUID();
        let publishRequest = rootState.ui.publish.request;

        publishRequest.betaUrlPart = uniqueId;
        publishRequest.urlPart = uniqueId;
    }

    private _designNameToSafeUrlPart(designName:string):string
    {
        if( !designName )
            throw new Error(`Invalid designName: ${designName}`);

        return designName
            .replace(/(\s)+/g, '-')
            .toLowerCase()
            .replace(/[^a-z\d-]/g, '');
    }

    @action
    public setPublishMode(mode:PublishMode):void
    {
        const {openDesign} = rootState.ui.visualDesigner;

        const uiRequest = rootState.ui.publish.request;
        const publishState = openDesign
            ? openDesign.designState.assets.publishState
            : PublishState.create();

        if( mode === PublishMode.UniqueLink )
        {
            this._setUrlPartToUniqueId();
        }
        else if( mode === PublishMode.Public )
        {
            let urlPart:string = this._getUrlPartIfPublished();

            if( !urlPart )
                urlPart = uiRequest.isBeta ? publishState.betaUrlPart : publishState.urlPart;

            this.setUrlPart(urlPart);

            this._fetchUrlPartSuggestions(uiRequest.designRef.designId);
        }

        uiRequest.mode = mode;
    }

    public _getUrlPartIfPublished(props?:{isBeta?:boolean, designRef?:DesignRef}):string | null
    {
        const {designRef, isBeta} = rootState.ui.publish.request;

        const _designRef = (props && props.designRef) || designRef;

        if( !_designRef ) return null;

        const design:PublishedDesignResponse = controllers.ui.mainPage.getPublishedDesignById(_designRef.designId);

        const _isBeta:boolean = props && props.isBeta !== undefined ? props.isBeta : isBeta;

        const version:PublishedApplicationResponse = design && design.versions &&
            design.versions.filter((ver) => ver.beta === _isBeta)[0];

        return version && version.urlPart ? version.urlPart : null;
    }

    @action
    public setUrlPart(urlPart:string):void
    {
        let publishRequest = rootState.ui.publish.request;
        if( publishRequest.isBeta )
        {
            publishRequest.betaUrlPart = urlPart;
        }
        else
        {
            publishRequest.urlPart = urlPart;
        }
    }

    @action
    public async validateUrlPart(newValue:string, state:IFormFieldState<string>):Promise<FormFieldValidationResult>
    {
        this._updatePublishMessage(PublishStatus.CheckingUrl);

        const isBeta = rootState.ui.publish.request.isBeta;
        const localValidationResult = this.validateUrlPartInplace(newValue, isBeta);

        if( !localValidationResult.isValid )
            return localValidationResult;

        return API.publish.checkUrlPart(newValue, rootState.ui.publish.request.designRef.designId, isBeta)
            .then((resp:CheckResponse) =>
            {
                if( resp.allowed )
                    return new FormFieldValidationResult(true, '');
                else
                    return new FormFieldValidationResult(false, 'That application URL is already being used. Try another.');
            })
            .catch(() =>
            {
                return new FormFieldValidationResult(false, 'Error! Please contact us for support.');
            });
    }

    private validateUrlPartInplace(urlPart:string, isBeta:boolean)
    {
        if( !urlPart || urlPart.length == 0 )
            return new FormFieldValidationResult(false, 'url part should not be empty');

        if( urlPart.length > 63 )
            return new FormFieldValidationResult(false, 'url part must be between 1 and 63 symbols');

        const {siteId} = rootState.userSession.currentOrganization;

        const maxUrlPartLength = PublishedAppUrlUtil.maxUrlPartLength(siteId, isBeta);
        if( urlPart.length > maxUrlPartLength )
            return new FormFieldValidationResult(false, `url part should not exceed ${maxUrlPartLength} characters, currently ${urlPart.length}`);

        const regex = /^[a-z\d-]+$/;
        if( !regex.test(urlPart) )
        {
            const validCharRegex = /[a-z\d]/g;
            const invalidSymbols = urlPart.replace(validCharRegex, '');
            var invalidSymbolsUnique = invalidSymbols.split('').filter(function(item, i, ar)
            {
                return ar.indexOf(item) === i;
            }).join('');
            return new FormFieldValidationResult(false, `only lowercase letters, digits and hyphens allowed. invalid characters "${invalidSymbolsUnique}"`);
        }
        return new FormFieldValidationResult(true, '');
    }

    @action
    public setIsBeta(isBeta:boolean):void
    {
        const uiRequest = rootState.ui.publish.request;
        uiRequest.isBeta = isBeta;
    }

    @action
    public setMembersToInvite(emails:Array<string>):void
    {
        rootState.ui.publish.membersToInvite = emails;
    }

    @action
    public setCnameCheckId(cnameCheckId:string):void
    {
        rootState.ui.publish.cnameCheckId = cnameCheckId;
    }

    @action
    public validateDomain(domain:string, state:IFormFieldState<string>):FormFieldValidationResult
    {
        const isValid:boolean = !state.isDirty
            || (state.isFocused && !state.isSubmitted)
            || (domain && /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/.test(domain));

        rootState.ui.publish.domainValidationResult = new FormFieldValidationResult(
            isValid,
            isValid ? '' : 'Please enter a valid Domain'
        );

        return rootState.ui.publish.domainValidationResult;
    }

    @action
    public setCnameHostname(cnameHostname:string):void
    {
        rootState.ui.publish.cnameHostname = cnameHostname;
    }

    @action
    public showConnectDomainDialog():void
    {
        rootState.ui.publish.isConnectDomainMode = true;
        rootState.ui.publish.connectDomainState = ConnectDomainState.Default;
        this.setCnameHostname(rootState.ui.publish.request.cnameHostname);
    }

    @action
    public nextConnectDomainDialogStep():void
    {
        const publishState = rootState.ui.publish;
        const {
            connectDomainState,
            domainValidationResult
        } = publishState;

        if( connectDomainState === ConnectDomainState.Default && domainValidationResult && domainValidationResult.isValid )
        {
            publishState.connectDomainState = ConnectDomainState.DomainAdded;
        }
        else if( connectDomainState === ConnectDomainState.DomainAdded || connectDomainState === ConnectDomainState.Failure )
        {
            this.checkCustomerCNameStatus();
        }
    }

    @action
    public hideConnectDomainDialog():void
    {
        rootState.ui.publish.isConnectDomainMode = false;
    }

    @action
    public applyDomainSettings():void
    {
        const publishState = rootState.ui.publish;

        publishState.connectDomainState = ConnectDomainState.Confirmed;
        publishState.request.cnameHostname = publishState.cnameHostname;
        publishState.isConnectDomainMode = false;
    }

    @action.bound
    public setDomainSettingsFailure():void
    {
        const publishState = rootState.ui.publish;

        publishState.connectDomainState = ConnectDomainState.Failure;
    }

    @action.bound
    public publishOpenDesign(uiRequest:PublishUIRequest):void
    {
        const {shouldSaveBeforePublish} = rootState.ui.publish;
        const {multiplayerInstanceApiEndpoint} = rootState.net;
        this._resetPublishState();

        this.validateUrlPart(uiRequest.currentUrlPart, null)
            .then((validationResult:FormFieldValidationResult) =>
            {
                if( validationResult.isValid )
                {
                    const openDesign = controllers.ui.openDesign.getOpenDesign();

                    if( !shouldSaveBeforePublish || (openDesign && !openDesign.designState.savePromise && !openDesign.designState.hasSkippedSaves) )
                        return true;

                    this._updatePublishMessage(PublishStatus.SavingDesign);

                    return openDesign.designState.savePromise
                        ? openDesign.designState.savePromise
                        : controllers.ui.designSync.reSyncDesign();
                }
                else
                    throw new Error(validationResult.errorMessage);
            })
            .then((canBePublished:boolean) =>
            {
                if( canBePublished )
                {
                    const publishRequest = this._createPublishRequest(uiRequest);

                    return multiplayerInstanceApiEndpoint.publish
                        .publish(publishRequest)
                        .then((response:PublishedApplicationResponse | ApiError) =>
                        {
                            if( !(response instanceof PublishedApplicationResponse) )
                                throw new Error(response.message);

                            this._pollUntilDoneOrError(response.id, uiRequest);
                        });
                }
                else
                {
                    throw new Error('Could not save design. Please contact us for support.');
                }
            })
            .catch((err:Error) =>
            {
                this._setPublishFailure(err && err.message);
            });
    }

    @action.bound
    private _setPublishFailure(errorMessage:string = 'Failed to publish. Please contact support.'):void
    {
        const publishProgress = rootState.ui.publish.progress;

        publishProgress.isPublishInProgress = false;
        publishProgress.publishProgressMessage = errorMessage;
        publishProgress.hadErrorDuringPublish = true;
        publishProgress.isPublished = false;

        this._setPublishFinished();
    }

    @action.bound
    private _setPublishSuccess(uiRequest:PublishUIRequest):void
    {
        const publishProgress = rootState.ui.publish.progress;
        const publishRequest = rootState.ui.publish.request;
        const openDesign = rootState.ui.visualDesigner.openDesign;

        publishProgress.isPublishInProgress = false;
        publishProgress.publishProgressMessage = null;
        publishProgress.hadErrorDuringPublish = false;
        publishProgress.isPublished = true;

        if( openDesign )
        {
            const {
                designState: {
                    assets: {
                        publishState
                    }
                }
            } = openDesign;

            publishState.mode = publishRequest.mode;
            publishState.isBeta = publishRequest.isBeta;

            if( publishRequest.isBeta )
                publishState.betaUrlPart = publishRequest.betaUrlPart;
            else
                publishState.urlPart = publishRequest.urlPart;
        }

        const {currentOrganization} = rootState.userSession;
        const {designRef} = uiRequest;
        const {projectId, designId, designName} = designRef;
        const {projects} = rootState.data;
        const project:Project = projects.filter((prj:Project) => prj.id === projectId)[0];
        const hostName = WindowLocationUtil.getHostname();
        const urlPart = publishRequest.isBeta ? publishRequest.betaUrlPart : publishRequest.urlPart;

        controllers.analytics.main.trackEvent(
            AnalyticsEventActions.WSP_DESIGN_PUBLISHED,
            {
                org_sid: currentOrganization.siteId,
                org_name: currentOrganization.name,

                space_id: projectId,
                space_name: project ? project.name : '',

                design_id: designId,
                design_name: designName,

                mode: publishRequest.mode,
                url: `${urlPart}-${hostName}`
            }
        );

        this._setPublishFinished();
    }

    @action.bound
    private _setPublishFinished():void
    {
        controllers.ui.publishedApps.init();
    }

    @action
    private _resetPublishState():void
    {
        const publishProgress = rootState.ui.publish.progress;

        publishProgress.isPublishInProgress = true;
        publishProgress.publishProgressMessage = null;
        publishProgress.hadErrorDuringPublish = false;
        publishProgress.isPublished = false;
    }

    private _createPublishRequest(req:PublishUIRequest):PublishRequest
    {
        const projectId = req.designRef.projectId;
        const designId = req.designRef.designId;
        const designSettingsId = null;
        const mode = req.mode;
        const beta:boolean = req.isBeta;
        const urlPart = req.currentUrlPart;

        const hostname = req.cnameHostname;
        const cname = hostname && hostname.length > 0 ? hostname : null;

        return new PublishRequest(
            projectId,
            designId,
            designSettingsId,
            cname,
            urlPart,
            mode,
            beta
        );
    }

    private _pollUntilDoneOrError(publishedAppId:number, uiRequest:PublishUIRequest):void
    {
        API.publish.getPublish(publishedAppId)
            .then((resp:PublishedApplicationResponse) =>
            {
                this._updatePublishState(resp);

                const publishState = rootState.ui.publish.progress;

                if( publishState.isPublishInProgress )
                    setTimeout(() => this._pollUntilDoneOrError(publishedAppId, uiRequest), 300);
                else if( !publishState.isPublishInProgress && !publishState.hadErrorDuringPublish )
                    this._setPublishSuccess(uiRequest);
                else
                    this._setPublishFinished();

            })
            .catch((response:PublishedApplicationResponse) =>
            {
                this._setPublishFailure();
            });
    }

    @action
    private _updatePublishState(resp:PublishedApplicationResponse):void
    {
        const newStatus:PublishStatus = resp.status;

        this._updatePublishBooleanState(newStatus);
        this._updatePublishMessage(newStatus);
    }

    @action
    private _updatePublishMessage(newStatus:PublishStatus):void
    {
        const publishState = rootState.ui.publish.progress;

        switch( newStatus )
        {
            case PublishStatus.CheckingUrl:
                publishState.publishProgressMessage = 'Checking publish URL availability...';
                break;
            case PublishStatus.SavingDesign:
                publishState.publishProgressMessage = 'Saving design...';
                break;
            case PublishStatus.Scheduled:
                publishState.publishProgressMessage = 'Starting to publish...';
                break;
            case PublishStatus.Preparing_Assets:
                publishState.publishProgressMessage = 'Preparing a few client bits...';
                break;
            case PublishStatus.Deploying_Topology:
                publishState.publishProgressMessage = 'Spinning the cloud bits...';
                break;
            case PublishStatus.Running:
                publishState.publishProgressMessage = 'Published!';
                break;
            case PublishStatus.Failed:
                publishState.publishProgressMessage = 'Failed to publish. Please contact support.';
                break;
            case PublishStatus.Stopping:
                publishState.publishProgressMessage = 'Unpublishing...';
                break;
            case PublishStatus.Stopped:
                publishState.publishProgressMessage = `Unpublished!`;
                break;
            case PublishStatus.Stop_Failed:
                publishState.publishProgressMessage = `Couldn't unpublish. Please contact support.`;
                break;
        }
    }

    private _updatePublishBooleanState(newStatus:PublishStatus):void
    {
        const publishState = rootState.ui.publish.progress;

        switch( newStatus )
        {
            case PublishStatus.Scheduled:
            case PublishStatus.Preparing_Assets:
            case PublishStatus.Deploying_Topology:
            case PublishStatus.Stopping:
                publishState.isPublishInProgress = true;
                break;
            case PublishStatus.Running:
            case PublishStatus.Stopped:
                publishState.isPublishInProgress = false;
                break;
            case PublishStatus.Failed:
            case PublishStatus.Stop_Failed:
                publishState.isPublishInProgress = false;
                publishState.hadErrorDuringPublish = true;
                break;
        }
    }
}
